import { useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FileUp, Loader2 } from "lucide-react";

export const TrackingImport = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setPreview(null); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/container-tracking/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); e.target.value = ""; }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/container-tracking/import/commit", { preview_id: preview.preview_id });
      const s = r.data.summary;
      toast.success(`Tracking import committed — ${s.applied} applied, ${s.flagged} flagged for review, ${s.rejected} rejected`);
      setOpen(false);
      reset();
      onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="tracking-import-button" className="h-10 border-limestone text-charcoal hover:bg-sand"><FileUp className="mr-2 h-4 w-4" /> Import broker update</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] overflow-y-auto border-border bg-white sm:max-w-3xl" data-testid="tracking-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium text-charcoal">Import broker tracking update</DialogTitle>
          <DialogDescription className="text-sm text-warmgrey">CSV / Excel with columns <span className="font-mono">Container, Event, Date</span> (optional <span className="font-mono">Location, ETA, Vessel, Voyage, Note</span>). Nothing is written until you confirm. Tracking imports never move inventory.</DialogDescription>
        </DialogHeader>
        {!preview ? (
          <div className="mt-2">
            <input id="tracking-import-file" type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={onFile} className="sr-only" data-testid="tracking-import-file" />
            <label htmlFor="tracking-import-file" className="flex h-28 cursor-pointer items-center justify-center rounded-md border border-dashed border-limestone bg-sand/30 text-sm text-charcoal hover:bg-sand" data-testid="tracking-import-dropzone">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Tap to choose the broker's CSV or Excel file"}
            </label>
          </div>
        ) : (
          <div className="mt-2 space-y-4" data-testid="tracking-import-preview">
            <div className="flex flex-wrap gap-4 text-xs">
              <span data-testid="import-accepted">Accepted <strong>{preview.accepted_rows}</strong></span>
              <span data-testid="import-flagged" className="text-amber-800">Flagged for review <strong>{preview.flagged_rows}</strong></span>
              <span data-testid="import-rejected" className="text-red-700">Rejected <strong>{preview.rejected_rows}</strong></span>
              {preview.already_imported && <span className="text-amber-800" data-testid="import-already">This exact file was already imported — duplicates will be rejected</span>}
            </div>
            <div className="max-h-72 overflow-auto rounded-md border border-border">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-sand/60 text-left uppercase tracking-wider text-warmgrey"><tr><th className="px-2 py-2">Row</th><th className="px-2 py-2">Container</th><th className="px-2 py-2">Event</th><th className="px-2 py-2">Date</th><th className="px-2 py-2">ETA</th><th className="px-2 py-2">Result</th></tr></thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.row} className="border-t border-border align-top" data-testid={`import-row-${r.row}`}>
                      <td className="px-2 py-2">{r.row}</td>
                      <td className="px-2 py-2">{r.container_ref}{r.zc_ref ? <span className="block text-warmgrey">{r.zc_ref}</span> : null}</td>
                      <td className="px-2 py-2">{r.event_type ? r.event_type.replace(/_/g, " ") : r.event_text}</td>
                      <td className="px-2 py-2">{r.date}</td>
                      <td className="px-2 py-2">{r.eta || "—"}</td>
                      <td className="px-2 py-2">
                        {r.ok ? (r.flags.length ? <span className="text-amber-800">Review: {r.flags.map((f) => f.code).join(", ")}</span> : <span className="text-sage">{r.stage_change ? "Advances stage" : r.late_historical ? "Historical (no regress)" : "OK"}</span>) : <span className="text-red-700">{r.errors.join("; ")}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" onClick={reset} disabled={busy} data-testid="import-choose-another" className="h-11 border-limestone">Choose another file</Button>
              <Button onClick={commit} disabled={busy || preview.accepted_rows === 0} data-testid="import-commit" className="h-11 flex-1 bg-terracotta text-white hover:bg-terracotta-hover">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Confirm import (${preview.accepted_rows} row${preview.accepted_rows === 1 ? "" : "s"})`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
